import { FC, useState } from 'react'
import { AppButton, AppForm, AppFormItem, AppPasswordInput, useAppForm } from '@/shared/ui'
import { useTranslation } from 'react-i18next'
import {
  buttonStyle,
  passwordStrengthTransitionStyle,
  passwordStrengthHiddenStyle
} from './ChangePasswordInputs.styles'
import { ChangePasswordFormData, ChangePasswordInputsProps } from './ChangePasswordInputs.types'
import { useChangePasswordValidations } from '../../../hooks/useChangePasswordValidations'
import PasswordStrength from '../PasswordStrength/PasswordStrength'

const ChangePasswordInputs: FC<ChangePasswordInputsProps> = ({
  onSubmit,
  loading = false,
  submitButtonText,
  validationConfig,
  disabled = false
}) => {
  const { t } = useTranslation()
  const [form] = useAppForm()
  const [currentPassword, setCurrentPassword] = useState('')
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false)
  const [hasStartedTyping, setHasStartedTyping] = useState(false)
  const { validateAllRules } = useChangePasswordValidations(validationConfig)

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCurrentPassword(value)
    if (value.length > 0 && !hasStartedTyping) {
      setHasStartedTyping(true)
    }
  }

  const handlePasswordFocus = () => {
    setIsPasswordInputFocused(true)
  }

  const handlePasswordBlur = () => {
    setIsPasswordInputFocused(false)
  }

  const handleSubmit = async (values: ChangePasswordFormData) => {
    if (!onSubmit || loading || disabled) return
    onSubmit(values)
  }

  const passwordRules = [
    {
      required: true,
      message: ''
    },
    {
      validator: (_: any, value: string) => {
        const error = validateAllRules(value)
        return error ? Promise.reject() : Promise.resolve()
      }
    }
  ]

  const confirmPasswordRules = [
    {
      required: true,
      message: ''
    },
    ({ getFieldValue }: any) => ({
      validator(_: any, value: string) {
        if (!value || getFieldValue('new_password') === value) {
          return Promise.resolve()
        }
        return Promise.reject(new Error(t('password_strength.password_not_match')))
      }
    })
  ]

  return (
    <div>
      <AppForm form={form} layout='vertical' onFinish={handleSubmit}>
        <AppFormItem label={t('user-profile.second-table.new-pass')} name='new_password' rules={passwordRules}>
          <AppPasswordInput
            placeholder={t('user-profile.second-table.enter-new-password')}
            disabled={disabled}
            onChange={handlePasswordChange}
            onFocus={handlePasswordFocus}
            onBlur={handlePasswordBlur}
          />
        </AppFormItem>

        <div style={hasStartedTyping ? passwordStrengthTransitionStyle : passwordStrengthHiddenStyle}>
          <PasswordStrength
            password={currentPassword}
            showDetailedView={!isPasswordInputFocused}
            validationConfig={validationConfig}
          />
        </div>

        <AppFormItem
          label={t('user-profile.second-table.confirm-pass')}
          name='confirm_new_password'
          dependencies={['new_password']}
          rules={confirmPasswordRules}
        >
          <AppPasswordInput placeholder={t('user-profile.second-table.enter-confirm-password')} disabled={disabled} />
        </AppFormItem>

        <AppFormItem>
          <AppButton fullWidth style={buttonStyle} htmlType='submit' loading={loading} disabled={disabled}>
            {submitButtonText || t('user-profile.second-table.title')}
          </AppButton>
        </AppFormItem>
      </AppForm>
    </div>
  )
}

export default ChangePasswordInputs
